<style lang="stylus">
.add-courses {
  background: url('../assets/icons/bg_image.png') center / cover no-repeat;
}

.search-results {
  color: rgb(40, 149, 188);
  font-weight: 600;
}

.filter-col {
  padding: 4px !important;
}

.course-search {
  width: 180px;
  font-weight: 500;
  background: #191919;
  color: #fff;
}

/* Desktop sticky positioning for section headers */
@media (min-width: 960px) {
  .course-header-sticky {
    position: sticky !important;
    top: 0px !important;
    z-index: 12 !important;
    border-radius: 8px 8px 0 0 !important;
    margin-left: -20px !important;
    margin-right: -20px !important;
    margin-top: -20px !important;
    padding: 24px !important;
  }
}

/* Mobile - no sticky positioning */
@media (max-width: 959.98px) {
  .course-header-sticky {
    position: static !important;
    border-radius: 0 !important;
    margin: 0 !important;
    padding: 0px 0px 24px 0px!important;
  }
}

/* When both sections are available (section tabs are shown), use 80px top - desktop only */
@media (min-width: 960px) {
  .section-tabs-container ~ .v-row .course-header-sticky {
    top: 50px !important;
  }
}

/* Make sure the container allows proper sticking */
.course-section .pa-6 {
  position: relative;
  overflow: visible !important;
}

.selected-courses-col {
  align-self: flex-start !important;
  position: sticky !important;
  top: 50px !important;
  z-index: 5 !important;
  max-height: calc(100vh - 50px) !important;
  overflow-y: auto !important;
  padding-top: 0 !important;
}

.sidebar-content {
  transition: margin-top 0.2s ease;
}

.course-search-header {
  font-weight: 400;
  margin: 0 0 0 5px;
  font-size: 16px;
  color: #fff !important;
  padding: 1em 0em;
}

.tab-footer-view {
  @media (min-width: 750px) and (max-width: 900px) {
    padding-bottom: 6%;
  }
}

.section-tabs-wrapper {
  position: sticky;
  top: 0;
  background: white;
  z-index: 15;
}

.section-tabs-container {
  padding: 0px 4px;
  border-bottom: 2px solid #d0d0d0;
}

.section-tab {
  padding: 8px 16px;
  font-family: Arial;
  font-size: 24px;
  font-weight: 400;
  letter-spacing: -0.72px;
  line-height: 28px;
  color: #191919;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  border-bottom: 8px solid transparent;
  text-align: center;
}

/* Mobile styles for section tabs */
@media (max-width: 959.98px) {
  .section-tab {
    color: var(--asu-text-colors-primary-text, #191919);
    font-family: Arial;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.48px;
    flex: 1;
  }
}

/* Create invisible bold text to reserve space */
.section-tab::before {
  content: attr(data-text);
  font-weight: 700;
  height: 0;
  visibility: hidden;
  overflow: hidden;
  user-select: none;
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
}

.section-tab:hover {
  color: #000;
}

.section-tab--active {
  font-weight: 700;
  color: #191919;
  border-bottom-color: #ffc627;
}

/* Mobile active state */
@media (max-width: 959.98px) {
  .section-tab--active {
    color: var(--asu-text-colors-primary-text, #191919);
    font-weight: 700;
  }
}

.course-section {
  scroll-margin-top: 100px;
}

.main-content-wrapper {
  position: relative;
  overflow: visible;
}
</style>

<template>
  <div v-if="isUserAuthenticated">
    <!-- Search Header -->
    <div>
      <v-container class="container pa-0">
        <h1 style="font-size: 32px; font-weight: 700; margin-bottom: 8px; color: #191919">Add courses</h1>
        <p style="font-size: 16px; margin-bottom: 24px; color: #484848">
          Search our course catalog or view all available courses below.
        </p>

        <CourseFilters
          v-model:search-term="searchTerm"
          :filter-options="filterOptions"
          :filter-state="filterState"
          @update:filter-state="filterState = $event"
          @filter-select="handleFilterSelect"
          @clear-filters="handleClearAllFilters"
        />
      </v-container>
    </div>

    <!-- Main content -->
    <div class="main-content-wrapper">
      <v-container class="container pa-0" style="min-height: 500px">
        <!-- Search results count - always show when initialized -->
        <p
          v-if="coursesInitialized"
          class="search-results pt-2 pr-3"
          style="margin: 0; color: #747474; font-size: 16px"
        >
          Your search returned {{ filteredCoursesCount }} Results
        </p>

        <!-- Section tabs - positioned to align with courses column -->
        <div v-if="showSectionTabs" class="section-tabs-wrapper mt-5">
          <v-row no-gutters>
            <v-col cols="12" md="8" lg="8" xl="9">
              <div class="section-tabs-container">
                <div class="d-flex align-center justify-start">
                  <div
                    v-for="tab in sectionTabs"
                    :key="tab.id"
                    class="section-tab"
                    :class="{ 'section-tab--active': activeTab === tab.id }"
                    :data-text="tab.label"
                    @click="scrollToSection(tab.id)"
                  >
                    {{ tab.label }}
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <v-row class="mt-6">
          <!-- Courses list -->
          <v-col cols="12" md="8" lg="8" xl="9" class="mb-8 pa-0">
            <!-- No courses message - show when no courses are available for this partner -->
            <div v-if="courses.length === 0 && coursesInitialized" class="px-3">
              <p style="font-weight: 500">
                {{ noCourseMessage }}
                <a href="mailto:ulcourses@asu.edu" style="color: #8c1d40; text-decoration: underline"
                  >ulcourses@asu.edu</a
                >
                for assistance.
              </p>
            </div>

            <!-- Courses sections - structured with pay-to-enroll between partner-pay and other sections -->
            <div
              v-for="(section, sectionIndex) in orderedCourseSections"
              :key="section.type"
              :class="{ 'mt-8': sectionIndex > 0 }"
            >
              <div v-if="section.courses.length > 0">
                <!-- Special design for cost covered section -->
                <div
                  v-if="section.type === 'EnrollmentTypePartnerPay'"
                  :id="`section-${section.type}`"
                  class="mb-6 course-section"
                >
                  <div class="pa-6 rounded-lg" style="background: #fafafaff; position: relative">
                    <!-- Sticky header section with content and illustrations -->
                    <div class="course-header-sticky" style="background: #fafafaff">
                      <!-- Desktop Layout - Hidden on mobile -->
                      <div class="d-none d-md-flex align-center justify-space-between" style="gap: 32px">
                        <!-- Left Content Section -->
                        <div class="flex-grow-1 w-100">
                          <!-- Title -->
                          <h2
                            class="font-weight-bold mb-3 text-h5"
                            style="color: #8c1d40; letter-spacing: -0.02em; line-height: 1.17"
                          >
                            {{ section.title }}
                          </h2>

                          <!-- Description -->
                          <p class="mb-0 text-body-2" style="color: #000000; line-height: 24px">
                            {{ sectionsMetadata.EnrollmentTypePartnerPay?.tooltip }}
                          </p>
                        </div>

                        <div
                          class="d-flex align-center justify-center flex-shrink-0 mt-8"
                          style="width: 120px; height: 80px"
                        >
                          <!-- Books Guy Illustration -->
                          <img
                            src="@/assets/icons/books-guy.svg"
                            alt="Books illustration"
                            class="w-auto h-auto"
                            style="max-width: 120px; max-height: 80px"
                          />
                        </div>
                      </div>

                      <!-- Mobile Layout - Visible on mobile only -->
                      <div class="d-flex d-md-none flex-column" style="gap: 8px">
                        <!-- Title -->
                        <h2
                          class="font-weight-bold my-0 text-h5"
                          style="color: #8c1d40; letter-spacing: -0.02em; line-height: 1.17"
                        >
                          {{ section.title }}
                        </h2>

                        <!-- Description and SVG side by side -->
                        <div class="d-flex align-center" style="gap: 16px">
                          <!-- Description -->
                          <div class="flex-grow-1">
                            <p class="mb-0 text-body-2" style="color: #000000; line-height: 24px">
                              {{ sectionsMetadata.EnrollmentTypePartnerPay?.tooltip }}
                            </p>
                          </div>

                          <!-- Books Guy Illustration -->
                          <div
                            class="d-flex align-center justify-center flex-shrink-0"
                            style="width: 100px; height: 72px"
                          >
                            <img
                              src="@/assets/icons/books-guy.svg"
                              alt="Books illustration"
                              class="w-auto h-auto"
                              style="max-width: 100px; max-height: 72px"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Course list inside the grey container -->
                    <div class="d-flex flex-column" style="gap: 16px">
                      <CourseCard
                        v-for="(course, index) in section.courses"
                        :key="`course-${course.courseId}-${course.offering?.courseOfferingId || 'no-offering'}-${index}`"
                        :course="course"
                        :is-last-course="index === section.courses.length - 1"
                        :is-selected="isCourseSelected(course)"
                        :show-pathway-badge="true"
                        :dropdown-open="openDropdownCourseId === `${course.courseId}-${course.payment?.enrollmentType}`"
                        @add-course="handleAddCourse"
                        @remove-course="handleRemoveCourse"
                        @dropdown-toggle="
                          handleDropdownToggle(`${course.courseId}-${course.payment?.enrollmentType}`, $event)
                        "
                      />
                    </div>
                  </div>
                </div>
                <!-- Special highlight design for pay-to-enroll section -->
                <div
                  v-else-if="section.type === 'EnrollmentTypeLearnerPay'"
                  :id="`section-${section.type}`"
                  class="mb-6 course-section"
                >
                  <!-- Pay to enroll section -->
                  <div
                    class="pa-6 rounded-lg"
                    style="background: #fef2d0ff; border: 1px solid #e6d6bc; position: relative"
                  >
                    <!-- Sticky header section with content and illustrations -->
                    <div class="course-header-sticky" style="background: #fef2d0ff">
                      <div class="d-flex flex-column flex-md-row align-center justify-space-between ga-6">
                        <!-- Left Content Section -->
                        <div class="flex-grow-1 w-100">
                          <!-- Title -->
                          <h2
                            class="font-weight-bold my-0 mb-3 text-h5"
                            style="color: #8c1d40; letter-spacing: -0.02em; line-height: 1.17"
                          >
                            {{ section.title }}
                          </h2>

                          <!-- Description -->
                          <p
                            v-if="sectionsMetadata.EnrollmentTypeLearnerPay?.tooltip"
                            class="mb-3 text-body-2"
                            style="color: #191919; line-height: 24px"
                          >
                            {{ sectionsMetadata.EnrollmentTypeLearnerPay?.tooltip }}
                          </p>

                          <!-- Key Facts -->
                          <div class="d-flex align-center ga-6">
                            <!-- Facts Content -->
                            <div class="d-flex flex-column flex-grow-1 ga-4">
                              <!-- $25 to enroll -->
                              <div class="d-flex align-center">
                                <div class="mr-4 flex-shrink-0">
                                  <div
                                    class="d-flex align-center justify-center rounded-circle pa-4"
                                    style="width: 48px; height: 48px; background: white"
                                  >
                                    <img
                                      src="@/assets/icons/fee-icon.svg"
                                      alt="Fee icon"
                                      style="width: 21px; height: auto"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <div class="d-flex align-center ga-2 mb-1">
                                    <span
                                      class="font-weight-bold"
                                      style="
                                        color: #8c1d40;
                                        font-size: 20px;
                                        letter-spacing: -0.015em;
                                        line-height: 1.3;
                                      "
                                      >$25</span
                                    >
                                    <span
                                      class="font-weight-bold"
                                      style="
                                        color: #000000;
                                        font-size: 20px;
                                        letter-spacing: -0.015em;
                                        line-height: 1.3;
                                      "
                                      >to enroll</span
                                    >
                                  </div>
                                  <div style="color: #191919; font-size: 14px; line-height: 1.286">
                                    Reserve your spot in the course and begin coursework.
                                  </div>
                                </div>
                              </div>

                              <!-- $400 credit conversion -->
                              <div class="d-flex align-center">
                                <div class="mr-4 flex-shrink-0">
                                  <div
                                    class="d-flex align-center justify-center rounded-circle pa-4"
                                    style="width: 48px; height: 48px; background: white"
                                  >
                                    <img
                                      src="@/assets/icons/graduation-cap-icon.svg"
                                      alt="Graduation cap icon"
                                      style="width: 24px; height: auto"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <div class="d-flex align-center ga-2 mb-1">
                                    <span
                                      class="font-weight-bold"
                                      style="
                                        color: #8c1d40;
                                        font-size: 20px;
                                        letter-spacing: -0.015em;
                                        line-height: 1.3;
                                      "
                                      >$400</span
                                    >
                                    <span
                                      class="font-weight-bold"
                                      style="
                                        color: #000000;
                                        font-size: 20px;
                                        letter-spacing: -0.015em;
                                        line-height: 1.3;
                                      "
                                      >credit conversion</span
                                    >
                                  </div>
                                  <div style="color: #191919; font-size: 14px; line-height: 1.286">
                                    Pay only if you're satisfied with your grade and want to add it to your transcript.
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Right Illustration Section - Hidden on mobile, visible on md+ -->
                            <div class="d-none d-md-flex align-center justify-center flex-shrink-0 h-100">
                              <!-- Calculator Girl Illustration -->
                              <img
                                src="@/assets/icons/frame-calc-girl.svg"
                                alt="Calculator illustration"
                                class="h-100 w-auto"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Course list inside the highlighted container -->
                    <div class="d-flex flex-column" style="gap: 16px">
                      <CourseCard
                        v-for="(course, index) in section.courses"
                        :key="`course-${course.courseId}-${course.offering?.courseOfferingId || 'no-offering'}-${index}`"
                        :course="course"
                        :is-last-course="index === section.courses.length - 1"
                        :is-selected="isCourseSelected(course)"
                        :show-pathway-badge="true"
                        :dropdown-open="openDropdownCourseId === `${course.courseId}-${course.payment?.enrollmentType}`"
                        @add-course="handleAddCourse"
                        @remove-course="handleRemoveCourse"
                        @dropdown-toggle="
                          handleDropdownToggle(`${course.courseId}-${course.payment?.enrollmentType}`, $event)
                        "
                      />
                    </div>
                  </div>
                </div>
                <!-- Standard section header for all other sections -->
                <div v-else :id="`section-${section.type}`" class="course-section">
                  <!-- Course list for standard sections -->
                  <div class="d-flex flex-column" style="gap: 16px">
                    <CourseCard
                      v-for="(course, index) in section.courses"
                      :key="`course-${course.courseId}-${course.offering?.courseOfferingId}`"
                      :course="course"
                      :is-last-course="index === section.courses.length - 1"
                      :is-selected="isCourseSelected(course)"
                      :show-pathway-badge="true"
                      :dropdown-open="openDropdownCourseId === course.courseId"
                      @add-course="handleAddCourse"
                      @remove-course="handleRemoveCourse"
                      @dropdown-toggle="handleDropdownToggle(course.courseId, $event)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </v-col>

          <!-- Selected courses sidebar -->
          <v-col cols="12" sm="12" md="4" lg="4" xl="3" class="selected-courses-col">
            <div v-if="!shouldHideSidebar" class="sidebar-content">
              <SelectedCoursesV2
                class="mb-6"
                :selected-courses="selectedCourses"
                :all-courses="courses"
                :should-show-note-under-schedule="shouldShowNoteUnderSchedule"
                @remove-course="handleRemoveCourse"
                @add-course="handleAddCourse"
                @review-selection="handleReviewSelection"
              />

              <college-courses v-scroll="onScroll" top="0" :scroll-y="checkEnrollmentSectionHeight" />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Confirmation modal for too many courses -->
    <GenericModal
      :show="dialog"
      title="Yikes! You're about to add more than the recommended course load"
      message="Are you sure you'd like to continue?"
      confirm-text="Yes, add all courses"
      cancel-text="No, go back"
      confirm-variant="gold"
      @confirm="handleConfirmOverload()"
      @cancel="handleCancelOverload()"
      @update:show="dialog = $event"
    >
      <div>
        We recommend a maximum course load of 2 courses per session so you have adequate time to complete your
        assignments.
      </div>
    </GenericModal>

    <!-- Partnership Course Warning Modal -->
    <CourseEnrollmentModal
      :show="showPartnershipWarningModal"
      type="partnership"
      :course="pendingCourseAction?.course || {}"
      @confirm="handlePartnershipWarningConfirm"
      @cancel="handlePartnershipWarningCancel"
      @update:show="showPartnershipWarningModal = $event"
    />

    <!-- Block access dialogs -->
    <AccessRestrictionsDialog
      :show="blockAccessToEnrollCourse"
      :block-admitted-student="blockAdmittedStudentToEnrollCourse"
      :block-disqualified-student="blockDisqualifiedStudentToEnrollCourse"
      @return-to-dashboard="handleReturnToDashboard"
      @view-catalog="redirectToCatalogPage"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useUserStore, useCoursesStore, useUiStore, useViewAsStore } from '@/stores';
import { DataLayerMixin } from '@/mixins/dataLayerMixin.js';
import { AjaxLoader } from '@/mixins/ajaxLoader.js';
import { SessionInitializer } from '@/mixins/sessionInitializer.js';
import { Logger } from '@/mixins/logger.js';
import { Helper } from '@/mixins/helpers.js';
import UiButton from '@/components/ui/Button.vue';
import GenericModal from '@/components/courses/GenericModal.vue';
import SelectedCoursesV2 from '@/components/SelectedCoursesV2.vue';
import InfoTooltip from '@/components/ui/InfoTooltip.vue';
import CourseCard from '@/components/courses/CourseCard.vue';
import SearchBar from '@/components/SearchBar.vue';
import { SessionIcon, InterestAreaIcon, CreditDesignationIcon, LengthIcon, CreditsIcon } from '@/components/icons';

// New refactored components and composables
import CourseFilters from '@/components/courses/CourseFilters.vue';
import CourseEnrollmentModal from '@/components/courses/CourseEnrollmentModal.vue';
import AccessRestrictionsDialog from '@/components/courses/AccessRestrictionsDialog.vue';
import { usePartnershipWarning } from '@/composables/courses/usePartnershipWarning.js';
import { resolveOfferingCanvasCourseId } from '@/utils/ga4Items';
import { CourseEnrollmentService } from '@/services/courses/enrollmentService.js';

export default defineComponent({
  name: 'AddCoursesV2',

  components: {
    UiButton,
    SelectedCoursesV2,
    InfoTooltip,
    GenericModal,
    CourseCard,
    SearchBar,
    SessionIcon,
    InterestAreaIcon,
    CreditDesignationIcon,
    LengthIcon,
    CreditsIcon,
    // New refactored components
    CourseFilters,
    CourseEnrollmentModal,
    AccessRestrictionsDialog
  },

  mixins: [DataLayerMixin, AjaxLoader, SessionInitializer, Logger, Helper],

  setup() {
    // Use the partnership warning composable
    const partnershipWarning = usePartnershipWarning();

    return {
      // Partnership warning functionality
      ...partnershipWarning
    };
  },

  props: {
    degreePath: {
      type: String
    },
    refreshDashboardPage: {
      type: Object
    },
    data: {
      type: Object
    }
  },

  data() {
    return {
      isUserAuthenticated: false,
      searchTerm: '',
      selectedCourses: [],
      dialog: false,
      lastAddedCourse: null, // Track the last course added to handle "No, go back"
      proceedAfterOverload: false, // Track if we should proceed to dashboard after overload confirmation
      hasShownOverloadWarning: false, // Track if we already showed the overload warning during course selection
      coursesInitialized: false,
      checkEnrollmentSectionHeight: 0,
      openDropdownCourseId: null, // Track which course's dropdown is currently open (using unique courseId-enrollmentType)

      // V2 API data
      shouldShowNoteUnderSchedule: false,

      // Unified filter state - consolidated from 5 separate arrays
      filterState: {
        sessions: [],
        interestAreas: [],
        creditDesignations: [],
        courseLengths: [],
        creditCounts: []
      },

      // Section tabs state
      activeTab: null,
      scrollListener: null
    };
  },

  computed: {
    blockAccessToEnrollCourse() {
      const coursesStore = useCoursesStore();
      const accessControl = coursesStore.learnerCourses?.learnerData?.accessControl;

      // Backend exposes the admitted-student block as `isBlocked`. This gate is not
      // dismissable — a restricted learner must leave via Return to Dashboard.
      return accessControl?.isBlocked || accessControl?.isDisqualified || false;
    },

    blockAdmittedStudentToEnrollCourse() {
      const coursesStore = useCoursesStore();

      return coursesStore.learnerCourses?.learnerData?.accessControl?.isBlocked || false;
    },

    blockDisqualifiedStudentToEnrollCourse() {
      const coursesStore = useCoursesStore();

      return coursesStore.learnerCourses?.learnerData?.accessControl?.isDisqualified || false;
    },

    // Get courses from store - direct state access
    courses() {
      const allCourses = useCoursesStore().learnerCourses?.courses || [];

      return allCourses.map((course) => {
        // Grey out pickerless "Add to Transcript" cards — this page has no button-action handler
        if (course.section?.name === 'Add to Transcript' && course.ui?.showDropdown === false) {
          return {
            ...course,
            ui: {
              ...course.ui,
              buttonLabel: 'Enrolled',
              buttonVariant: 'dark-outline',
              buttonDisabled: true,
              showDropdown: false
            }
          };
        }

        // Check if this course is already selected in another section
        const crossSectionConflict = this.getCrossSectionConflict(course);
        if (crossSectionConflict) {
          return {
            ...course,
            ui: {
              ...course.ui,
              buttonVariant: 'dark-outline',
              buttonDisabled: true,
              showDropdown: false,
              disabledTooltip: crossSectionConflict.tooltip
            },
            offerings: [] // Clear offerings to prevent dropdown from showing
          };
        }

        return course;
      });
    },

    // Get filter options from store - direct state access
    filterOptions() {
      const storeFilters = useCoursesStore().learnerCourses?.filterOptions || {};

      // Provide defaults if store doesn't have data yet
      return {
        sessions: this.sortSessionOptions(
          (storeFilters.sessions || [{ value: 'Any', label: 'Any' }]).map((s) => ({
            ...s,
            label: s.label === 'On Demand' ? 'On-demand' : s.label
          }))
        ),
        interestAreas: storeFilters.interestAreas || [{ value: 'Any', label: 'Any' }],
        creditDesignations: storeFilters.creditDesignations || [{ value: 'Any', label: 'Any' }],
        courseLengths: storeFilters.courseLengths || [{ value: 'Any', label: 'Any' }],
        creditCounts: storeFilters.creditCounts || [{ value: 'Any', label: 'Any' }]
      };
    },

    // Get summary from store - direct state access
    summary() {
      return useCoursesStore().learnerCourses?.summary || {};
    },

    // Get section metadata from store - includes tooltips based on benefit type
    sectionsMetadata() {
      return useCoursesStore().learnerCourses?.sections || {};
    },

    // Get no course message from store - varies by partner type
    noCourseMessage() {
      return useCoursesStore().learnerCourses?.noCourseMessage || 'No courses available at this time. Please contact';
    },

    // Get course grouping configuration from backend volatile settings
    isGroupingEnabled() {
      return useCoursesStore().learnerCourses?.isGroupingEnabled || false;
    },

    // Hide sidebar when showing partnership message
    shouldHideSidebar() {
      return (
        this.filteredCoursesCount === 0 &&
        this.coursesInitialized &&
        this.noCourseMessage.includes('Previous enrollment was covered by your high school partnership')
      );
    },

    /**
     * Format filters for SearchBar component
     * Parent defines everything: variant, icons, labels, options
     * SearchBar just renders what we give it
     */
    searchBarFilters() {
      // Helper to get label from value
      const getLabel = (options, value) => {
        const option = options.find((opt) => opt.value === value);
        return option ? option.label : value;
      };

      // Helper to create multi-select label
      const createMultiSelectLabel = (selectedArray, defaultLabel, options) => {
        if (selectedArray.length === 0 || selectedArray.includes('Any')) {
          return defaultLabel;
        }
        const firstLabel = getLabel(options, selectedArray[0]);
        return selectedArray.length > 1 ? `${firstLabel} +${selectedArray.length - 1}` : firstLabel;
      };

      // Filter configuration with icons and labels
      const filterConfigs = [
        { key: 'sessions', id: 'session', label: 'Session', icon: SessionIcon, optionsKey: 'sessions' },
        {
          key: 'interestAreas',
          id: 'interest-area',
          label: 'Interest area',
          icon: InterestAreaIcon,
          optionsKey: 'interestAreas'
        },
        {
          key: 'creditDesignations',
          id: 'credit-designation',
          label: 'Credit designation',
          icon: CreditDesignationIcon,
          optionsKey: 'creditDesignations'
        },
        { key: 'courseLengths', id: 'length', label: 'Length', icon: LengthIcon, optionsKey: 'courseLengths' },
        {
          key: 'creditCounts',
          id: 'credits',
          label: 'Number of credits',
          icon: CreditsIcon,
          optionsKey: 'creditCounts'
        }
      ];

      return filterConfigs.map((config) => {
        const selectedValues = this.filterState[config.key];
        const isActive = selectedValues.length > 0 && !selectedValues.includes('Any');
        const options = this.filterOptions[config.optionsKey] || [];

        return {
          id: config.id,
          label: createMultiSelectLabel(selectedValues, config.label, options),
          variant: isActive ? 'chip' : 'button',
          icon: config.icon,
          iconColor: isActive ? '#8C1D40' : '#484848',
          closable: true,
          multiSelect: true,
          selectedValues,
          options
        };
      });
    },

    /**
     * Enhanced courses with display formatting using CourseEnrollmentService
     * This demonstrates the service usage for better course presentation
     */
    enhancedCourses() {
      return this.courses.map((course) => {
        // Use service to enhance course display data
        return CourseEnrollmentService.formatCourseForDisplay(course);
      });
    },

    /**
     * Filter courses based on search and filter criteria
     */
    filteredCourses() {
      // Start with enhanced courses (including display formatting)
      let filtered = [...this.enhancedCourses];

      // Search term filter
      if (this.searchTerm) {
        const searchLower = this.searchTerm.toLowerCase();
        filtered = filtered.filter((course) => {
          const name = (course.courseName || '').toLowerCase();
          const prefix = (course.coursePrefix || '').toLowerCase();
          const prefixNoSpace = prefix.replace(/\s+/g, '');
          return name.includes(searchLower) || prefix.includes(searchLower) || prefixNoSpace.includes(searchLower);
        });
      }

      // Apply all filters using unified state
      const filters = [
        {
          key: 'sessions',
          check: (course) => {
            // Check if primary term matches
            if (this.filterState.sessions.includes(course.term?.sessionName)) {
              return true;
            }
            // Also check all offerings for matching sessions
            if (course.offerings && course.offerings.length > 0) {
              return course.offerings.some((offering) => this.filterState.sessions.includes(offering.sessionName));
            }
            return false;
          }
        },
        {
          key: 'interestAreas',
          check: (course) => {
            const track = course.academicInfo?.interestTrack || '';
            return this.filterState.interestAreas.some((selected) => track.includes(selected));
          }
        },
        {
          key: 'creditDesignations',
          check: (course) => {
            const courseDesignations = course.academicInfo?.creditDesignations || [];
            return this.filterState.creditDesignations.some((selected) => {
              const selectedCode = selected.split(':')[0].trim();
              return courseDesignations.some((d) => d.code === selectedCode);
            });
          }
        },
        {
          key: 'courseLengths',
          check: (course) => {
            // Check if primary length category matches
            if (this.filterState.courseLengths.includes(course.lengthCategory)) {
              return true;
            }
            // Also check all offerings for matching length categories
            if (course.offerings && course.offerings.length > 0) {
              return course.offerings.some((offering) => {
                const lengthCategory = this.mapOfferingLengthToCategory(offering.length);
                return this.filterState.courseLengths.includes(lengthCategory);
              });
            }
            return false;
          }
        },
        {
          key: 'creditCounts',
          check: (course) => {
            return this.filterState.creditCounts.some((selected) => {
              const creditValue = parseInt(selected);
              return course.credits === creditValue;
            });
          }
        }
      ];

      filters.forEach((filter) => {
        const selections = this.filterState[filter.key];
        // Apply filter only if there are selections (empty array means "Any" - no filtering)
        if (selections.length > 0) {
          filtered = filtered.filter(filter.check);
        }
      });

      // Filter offerings within each course based on applied filters
      filtered = filtered.map((course) => {
        if (!course.offerings || course.offerings.length === 0) {
          return course;
        }

        let filteredOfferings = [...course.offerings];

        // Apply session filter to offerings
        if (this.filterState.sessions.length > 0) {
          filteredOfferings = filteredOfferings.filter((offering) =>
            this.filterState.sessions.includes(offering.sessionName)
          );
        }

        // Apply course length filter to offerings
        if (this.filterState.courseLengths.length > 0) {
          filteredOfferings = filteredOfferings.filter((offering) => {
            // Map offering length to category
            const lengthCategory = this.mapOfferingLengthToCategory(offering.length);
            return this.filterState.courseLengths.includes(lengthCategory);
          });
        }

        return {
          ...course,
          offerings: filteredOfferings
        };
      });

      return filtered;
    },

    filteredCoursesCount() {
      // Count total offerings across all filtered courses instead of just course count
      return this.filteredCourses.reduce((total, course) => {
        return total + (course.offerings?.length || 0);
      }, 0);
    },

    /**
     * Course sections grouped by enrollment type
     * Configuration-driven approach to reduce code duplication
     * Controlled by MULTI_ENROLLMENT_TYPE_COURSE_GROUPING volatile setting from backend
     * Section metadata (title, tooltip) is now driven by backend based on benefit type
     */
    courseSections() {
      // Check if course grouping feature is enabled from backend volatile settings
      if (!this.isGroupingEnabled) {
        // Grouping disabled: Show all courses in a single section without grouping
        return [
          {
            type: 'All',
            title: null,
            tooltip: null,
            courses: this.filteredCourses
          }
        ];
      }

      // Grouping enabled: Use backend-provided section metadata for titles and tooltips
      const partnerPayMeta = this.sectionsMetadata.EnrollmentTypePartnerPay;
      const learnerPayMeta = this.sectionsMetadata.EnrollmentTypeLearnerPay;

      const sectionConfigs = [
        {
          type: 'EnrollmentTypePartnerPay',
          title: partnerPayMeta?.title,
          tooltip: partnerPayMeta?.tooltip,
          enrollmentTypes: ['EnrollmentTypePartnerPay']
        },
        {
          type: 'EnrollmentTypeLearnerPay',
          title: learnerPayMeta?.title,
          tooltip: learnerPayMeta?.tooltip,
          enrollmentTypes: ['EnrollmentTypeLearnerPay', 'FallbacktoBenefit']
        }
      ];

      // Build sections with filtered courses
      const sections = sectionConfigs.map((config) => {
        const sectionCourses = this.filteredCourses.filter((course) =>
          config.enrollmentTypes.includes(course.payment?.enrollmentType)
        );

        return {
          ...config,
          courses: sectionCourses
        };
      });

      return sections;
    },

    /**
     * Ordered course sections to ensure pay-to-enroll appears between covered and other sections
     * Order: 1. Partner Pay (covered), 2. Learner Pay (pay-to-enroll), 3. Others
     */
    orderedCourseSections() {
      const sections = this.courseSections;
      const orderedSections = [];

      // First: Add Partner Pay section (covered courses)
      const partnerPaySection = sections.find((s) => s.type === 'EnrollmentTypePartnerPay');
      if (partnerPaySection && partnerPaySection.courses.length > 0) {
        orderedSections.push(partnerPaySection);
      }

      // Second: Add Learner Pay section (pay-to-enroll)
      const learnerPaySection = sections.find((s) => s.type === 'EnrollmentTypeLearnerPay');
      if (learnerPaySection && learnerPaySection.courses.length > 0) {
        orderedSections.push(learnerPaySection);
      }

      // Third: Add any other sections that aren't the above two
      const otherSections = sections.filter(
        (s) => s.type !== 'EnrollmentTypePartnerPay' && s.type !== 'EnrollmentTypeLearnerPay' && s.courses.length > 0
      );
      orderedSections.push(...otherSections);

      return orderedSections;
    },

    /**
     * Show tabs only when both Program-supported and Pay to enroll sections have courses
     */
    showSectionTabs() {
      const partnerPaySection = this.orderedCourseSections.find((s) => s.type === 'EnrollmentTypePartnerPay');
      const learnerPaySection = this.orderedCourseSections.find((s) => s.type === 'EnrollmentTypeLearnerPay');

      return partnerPaySection?.courses.length > 0 && learnerPaySection?.courses.length > 0;
    },

    /**
     * Available section tabs
     */
    sectionTabs() {
      const tabs = [];

      this.orderedCourseSections.forEach((section) => {
        if (section.courses.length > 0) {
          tabs.push({
            id: section.type,
            label: section.title
          });
        }
      });

      return tabs;
    }
  },

  watch: {
    selectedCourses: {
      deep: true,
      handler() {
        if (
          document.getElementsByClassName('enrollment-section').length &&
          this.checkEnrollmentSectionHeight <
            parseInt(window.getComputedStyle(document.getElementsByClassName('enrollment-section')[0], null).height) +
              250
        ) {
          this.removeTop();
        } else {
          this.addTop();
        }
      }
    },

    'refreshDashboardPage.dashboard'() {
      this.initializeCoursesData();
    },

    // Watch for changes in section tabs to manage active tab
    showSectionTabs(newValue, oldValue) {
      this.$nextTick(() => {
        if (newValue && !oldValue) {
          // Tabs just became visible - set initial active tab
          const firstSection = this.sectionTabs[0];
          if (firstSection) {
            this.activeTab = firstSection.id;
          }
        } else if (!newValue && oldValue) {
          // Tabs hidden - clear active tab
          this.activeTab = null;
        }
      });
    }
  },

  mounted() {
    this.addTop();

    // Initialize scroll listener after component is mounted
    this.$nextTick(() => {
      // Always initialize scroll listener for sticky headers
      this.initializeScrollListener();

      if (this.showSectionTabs) {
        // Set initial active tab
        const firstSection = this.sectionTabs[0];
        if (firstSection) {
          this.activeTab = firstSection.id;
        }
      }
    });
  },

  beforeUnmount() {
    this.removeScrollListener();
  },

  methods: {
    /**
     * Sort session filter options chronologically (soonest first), with On-demand at the bottom.
     * "Any" stays at the top.
     */
    sortSessionOptions(sessions) {
      if (!sessions || sessions.length <= 1) return sessions;

      const SEASON_SORT_ORDER = { spring: 1, summer: 2, fall: 3 };
      const SUB_SESSION_SORT_ORDER = { a: 1, c: 2, b: 3 };

      const getSortKey = (label) => {
        const normalized = (label || '').toLowerCase();

        if (normalized === 'any') return { year: 0, seasonRank: 0, subSessionRank: 0 };
        if (normalized.includes('on-demand') || normalized.includes('on demand'))
          return { year: 9999, seasonRank: 0, subSessionRank: 0 };

        // Parse "Season Sub Year" format, e.g. "Spring B 2026"
        const tokens = label.split(' ');
        if (tokens.length >= 3) {
          const seasonRank = SEASON_SORT_ORDER[tokens[0].toLowerCase()] || 99;
          const subSessionRank = SUB_SESSION_SORT_ORDER[tokens[1].toLowerCase()] || 99;
          const year = parseInt(tokens[2]) || 9998;
          return { year, seasonRank, subSessionRank };
        }

        return { year: 9998, seasonRank: 99, subSessionRank: 99 };
      };

      return [...sessions].sort((a, b) => {
        const sortKeyA = getSortKey(a.label);
        const sortKeyB = getSortKey(b.label);
        if (sortKeyA.year !== sortKeyB.year) return sortKeyA.year - sortKeyB.year;
        if (sortKeyA.seasonRank !== sortKeyB.seasonRank) return sortKeyA.seasonRank - sortKeyB.seasonRank;
        return sortKeyA.subSessionRank - sortKeyB.subSessionRank;
      });
    },

    /**
     * Map offering length to category for filtering
     */
    mapOfferingLengthToCategory(offeringLength) {
      if (!offeringLength) return null;

      const length = offeringLength.toLowerCase();

      if (length.includes('8 week')) return '8 Weeks';
      if (length.includes('12 week')) return '12 Weeks';
      if (length.includes('16 week')) return '16 Weeks';
      if (length.includes('on-demand') || length.includes('min:') || length.includes('max:')) return 'On-demand';

      return null;
    },

    /**
     * Scroll to specific section
     */
    scrollToSection(sectionId) {
      const element = document.getElementById(`section-${sectionId}`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    },

    /**
     * Handle scroll event to update active tab and sidebar sticky state
     */
    handleScroll() {
      if (!this.showSectionTabs) return;

      const sections = this.sectionTabs
        .map((tab) => ({
          id: tab.id,
          element: document.getElementById(`section-${tab.id}`)
        }))
        .filter((section) => section.element);

      let activeSection = null;
      const scrollTop = window.scrollY + 150; // Offset to account for header

      // Find the section that's currently in view
      for (const section of sections) {
        const rect = section.element.getBoundingClientRect();
        const absoluteTop = rect.top + window.scrollY;

        if (scrollTop >= absoluteTop) {
          activeSection = section.id;
        }
      }

      if (activeSection && activeSection !== this.activeTab) {
        this.activeTab = activeSection;
      }

      // Handle sidebar sticky state
      this.handleSidebarStickyState();
    },

    /**
     * Handle sidebar sticky state to adjust margins
     */
    handleSidebarStickyState() {
      const sidebarCol = document.querySelector('.selected-courses-col');
      if (!sidebarCol) return;

      const rect = sidebarCol.getBoundingClientRect();
      const isSticky = rect.top <= 50; // Sticky threshold

      if (isSticky && !sidebarCol.classList.contains('sticky-active')) {
        sidebarCol.classList.add('sticky-active');
      } else if (!isSticky && sidebarCol.classList.contains('sticky-active')) {
        sidebarCol.classList.remove('sticky-active');
      }
    },

    /**
     * Initialize scroll listener
     */
    initializeScrollListener() {
      this.scrollListener = this.handleScroll.bind(this);
      window.addEventListener('scroll', this.scrollListener, { passive: true });
    },

    /**
     * Remove scroll listener
     */
    removeScrollListener() {
      if (this.scrollListener) {
        window.removeEventListener('scroll', this.scrollListener);
        this.scrollListener = null;
      }
    },

    /**
     * Handle filter selection from SearchBar - unified method
     * Note: CourseFilters handles the actual state update via update:filter-state
     * This method is primarily for analytics tracking
     */
    handleFilterSelect(data) {
      const { filter } = data;

      // Map filter IDs to filterState keys for analytics
      const filterKeyMap = {
        session: 'sessions',
        'interest-area': 'interestAreas',
        'credit-designation': 'creditDesignations',
        length: 'courseLengths',
        credits: 'creditCounts'
      };

      const filterKey = filterKeyMap[filter.id];
      if (!filterKey) return;

      // Track filter clicks for analytics only
      // The actual state update is handled by CourseFilters via update:filter-state event
      this.trackFilterAnalytics(filter.id, filterKey);
    },

    /**
     * Handle clear all filters from SearchBar - unified method
     */
    handleClearAllFilters() {
      // Reset entire filter state
      Object.keys(this.filterState).forEach((key) => {
        this.filterState[key] = [];
      });
    },

    /**
     * Generic analytics tracking for all filter interactions
     */
    trackFilterAnalytics(filterId, filterKey) {
      const selections = this.filterState[filterKey];

      // Only track if there are selections
      if (selections.length === 0) return;

      // Map filter IDs to display names for analytics
      const filterDisplayNames = {
        session: 'Session',
        'interest-area': 'Interest Area',
        'credit-designation': 'Credit Designation',
        length: 'Course Length',
        credits: 'Credits'
      };

      const displayName = filterDisplayNames[filterId] || filterId;

      this.asuoDataLayerClick('AddCourses', '0', '', {
        text: selections.join(','),
        type: displayName,
        action: 'click'
      });
    },

    /**
     * Load courses from V2 API using store action
     */
    async loadCoursesV2() {
      try {
        const userStore = useUserStore();
        const coursesStore = useCoursesStore();
        const learnerId = userStore.apis?.learnerData?._learnerId;

        if (!learnerId) {
          throw new Error('Learner ID not found');
        }

        this.logInfo('Fetching courses from V2 API', { learnerId });

        // Call store action to fetch and store courses data
        const response = await coursesStore.fetchCoursesForLearner(learnerId);

        // Store component-specific data
        this.shouldShowNoteUnderSchedule = response.shouldShowNoteUnderSchedule || false;

        return response;
      } catch (error) {
        this.logError('Failed to load courses from V2 API', error);
        throw error;
      }
    },

    /**
     * Initialize courses data
     */
    async initializeCoursesData() {
      this.isUserAuthenticated = true;

      // Load courses from V2 API - backend will handle access control and return early for restricted users
      await this.loadCoursesV2();

      // Handle URL query parameters
      if (this.$route.query?.searchCourse) {
        this.searchTerm = this.$route.query.searchCourse;
      }

      if (this.$route.query?.searchDesignation) {
        const designation = (this.filterOptions.creditDesignations || []).find((d) =>
          d.value.startsWith(this.$route.query.searchDesignation)
        );
        if (designation) {
          this.filterState.creditDesignations.push(designation.value);
        }
      }

      if (this.data?.coursePrefix) {
        this.searchTerm = this.data.coursePrefix;
      }

      this.coursesInitialized = true;
    },

    /**
     * Check if a course is currently selected
     * Returns true if ANY offering of this course is selected
     * Returns false for cross-section conflicts to hide "Added to schedule" button
     */
    isCourseSelected(course) {
      // If there's a cross-section conflict, don't show as selected
      const crossSectionConflict = this.getCrossSectionConflict(course);

      if (crossSectionConflict) return false;

      return this.selectedCourses.some((c) => c.courseId === course.courseId);
    },

    /**
     * Handle add course - from CourseCard or SelectedCourses
     */
    handleAddCourse(eventData) {
      // Event data can come from card click or sidebar
      let baseCourse;

      if (eventData.course) {
        // From CourseCard - use the exact course object to preserve enrollment type
        baseCourse = eventData.course;
      } else if (eventData.courseId) {
        // Fallback: find by courseId only
        baseCourse = this.courses.find((c) => c.courseId === eventData.courseId);
      } else if (eventData.coursePrefix) {
        // From SelectedCourses sidebar - use coursePrefix
        baseCourse = this.courses.find((c) => c.coursePrefix === eventData.coursePrefix);
      }

      if (!baseCourse) return;

      // Get the selected offering to check for offering-specific partnership warning
      let selectedOffering = null;

      if (eventData.courseOfferingId && baseCourse.offerings?.length > 0) {
        selectedOffering = baseCourse.offerings.find((o) => o.offeringId === eventData.courseOfferingId);
      }

      // Check if we need to show partnership warning (backend-provided flag)
      // Now check at offering level if a specific offering is selected
      if (this.shouldShowPartnershipWarning(baseCourse, selectedOffering)) {
        // Use composable to handle the warning
        this.handlePartnershipWarning(baseCourse, () => this.proceedWithCourseAction(eventData));
        return; // Don't proceed with enrollment yet
      }

      // If no warning needed, proceed with normal enrollment
      this.proceedWithCourseAction(eventData);
    },

    /**
     * Check if a course is already selected in a different enrollment type section
     * Returns conflict info if found, null otherwise
     */
    getCrossSectionConflict(course) {
      // Get the course prefix to check for same course across sections
      const coursePrefix = course.coursePrefix;

      // Check if this exact course is already in selectedCourses
      const isAlreadySelected = this.selectedCourses.some(
        (selectedCourse) => selectedCourse.coursePrefix === coursePrefix
      );

      if (!isAlreadySelected) {
        return null; // No conflict
      }

      // Find the selected course to determine which section it was added from
      const selectedCourse = this.selectedCourses.find((sc) => sc.coursePrefix === coursePrefix);
      const selectedEnrollmentType = selectedCourse.payment?.enrollmentType;
      const currentEnrollmentType = course.payment?.enrollmentType;

      // If it's the same enrollment type, no conflict (same section)
      if (selectedEnrollmentType === currentEnrollmentType) {
        return null;
      }

      // Generate appropriate tooltip based on where it was added from
      let tooltip = '';
      if (selectedEnrollmentType === 'EnrollmentTypePartnerPay') {
        tooltip =
          'You added this course under program-supported courses. You can remove this course there or in Your schedule.';
      } else if (selectedEnrollmentType === 'EnrollmentTypeLearnerPay') {
        tooltip = 'You added this course under pay-to-enroll. You can remove this course there or in Your schedule.';
      } else {
        tooltip = 'You added this course in another section. You can remove this course there or in Your schedule.';
      }

      return {
        tooltip,
        conflictingSection: selectedEnrollmentType
      };
    },

    /**
     * Actually perform the course enrollment (extracted from handleAddCourse)
     */
    proceedWithCourseAction(eventData) {
      // Event data can come from card click or sidebar
      let baseCourse;

      if (eventData.course) {
        // From CourseCard - use the exact course object to preserve enrollment type
        baseCourse = eventData.course;
      } else if (eventData.courseId) {
        // Fallback: find by courseId only
        baseCourse = this.courses.find((c) => c.courseId === eventData.courseId);
      } else if (eventData.coursePrefix) {
        // From SelectedCourses sidebar - use coursePrefix
        baseCourse = this.courses.find((c) => c.coursePrefix === eventData.coursePrefix);
      }

      if (!baseCourse) return;

      // Clone the course and update the offering to the selected one
      const course = { ...baseCourse };

      // If a specific offering was selected from dropdown, find it in the offerings array
      if (eventData.courseOfferingId && baseCourse.offerings?.length > 0) {
        const selectedOffering = baseCourse.offerings.find((o) => o.offeringId === eventData.courseOfferingId);

        if (selectedOffering) {
          // Update the course's offering to the selected one (use backend structure)
          course.offering = {
            courseOfferingId: selectedOffering.offeringId,
            canvasCourseId: resolveOfferingCanvasCourseId(selectedOffering, baseCourse.lms),
            startDate: selectedOffering.startDate,
            endDate: selectedOffering.endDate,
            eaCourseUrl: selectedOffering.courseUrl || '',
            isActive: true
          };

          // Update term information based on selected offering
          course.term = {
            ...course.term,
            sessionName: selectedOffering.sessionName
          };
        }
      }

      // Check if this specific offering is already selected
      const alreadySelected = this.selectedCourses.some(
        (c) => c.courseId === course.courseId && c.offering?.courseOfferingId === course.offering?.courseOfferingId
      );

      if (!alreadySelected) {
        this.selectedCourses.push(course);

        // Track this as the last added course (for potential removal if user cancels overload dialog)
        this.lastAddedCourse = course;

        // Update courses store
        const coursesStore = useCoursesStore();
        coursesStore.setSelectedCourses(this.selectedCourses);
        // Check if we now have more than 2 session-based courses with same start date
        this.checkCourseLoadAfterAdd();
      }
    },

    /**
     * Returns the largest count of Standard (session-based) courses within any single session.
     * A "session" is keyed by sessionName + offering startDate so e.g. Fall A 2026 and Fall A 2027
     * are not collapsed together.
     */
    maxStandardCoursesPerSession() {
      const countsBySession = new Map();

      this.selectedCourses.forEach((course) => {
        const courseType = (course.courseType || '').toLowerCase();
        if (courseType !== 'standard') return;

        const sessionKey = `${course.term?.sessionName || ''}|${course.offering?.startDate || ''}`;
        countsBySession.set(sessionKey, (countsBySession.get(sessionKey) || 0) + 1);
      });

      let max = 0;
      countsBySession.forEach((count) => {
        if (count > max) max = count;
      });
      return max;
    },

    /**
     * Check course load immediately after adding a course
     * Shows modal if any single session has more than 2 session-based courses (Standard type)
     */
    checkCourseLoadAfterAdd() {
      const hasOverload = this.maxStandardCoursesPerSession() > 2;

      if (hasOverload) {
        this.hasShownOverloadWarning = true;
        this.dialog = true;
      }
    },

    /**
     * Handle confirm on overload dialog - user wants to keep all courses
     */
    handleConfirmOverload() {
      this.dialog = false;

      // If triggered from "View schedule" button, proceed to enrollment
      if (this.proceedAfterOverload) {
        this.proceedAfterOverload = false;
        this.confirmEnrollment();
      }

      this.lastAddedCourse = null;
    },

    /**
     * Handle cancel on overload dialog - remove the last added course
     */
    handleCancelOverload() {
      this.logInfo('User cancelled course overload, removing last added course');

      if (this.lastAddedCourse) {
        // Remove the last added course
        const index = this.selectedCourses.findIndex(
          (c) =>
            c.courseId === this.lastAddedCourse.courseId &&
            c.offering?.courseOfferingId === this.lastAddedCourse.offering?.courseOfferingId
        );

        if (index >= 0) {
          this.selectedCourses.splice(index, 1);

          // Update courses store
          const coursesStore = useCoursesStore();
          coursesStore.setSelectedCourses(this.selectedCourses);

          this.logInfo('Removed last added course', {
            courseId: this.lastAddedCourse.courseId,
            coursePrefix: this.lastAddedCourse.coursePrefix
          });
        }

        this.lastAddedCourse = null;
      }

      // Reset warning flag since they removed the course
      this.hasShownOverloadWarning = false;
      this.proceedAfterOverload = false;
      this.dialog = false;
    },

    /**
     * Handle remove course - from CourseCard or SelectedCourses
     */
    handleRemoveCourse(eventData) {
      const index = this.selectedCourses.findIndex((c) => {
        if (eventData.courseId) {
          // From CourseCard "Added to schedule" button - match courseId only
          return c.courseId === eventData.courseId;
        } else if (eventData.coursePrefix) {
          // From SelectedCoursesV2 "Delete" link - match both coursePrefix and offeringId
          return (
            c.coursePrefix === eventData.coursePrefix && c.offering?.courseOfferingId === eventData.courseOfferingId
          );
        }
        return false;
      });

      if (index >= 0) {
        this.selectedCourses.splice(index, 1);

        // Update courses store
        const coursesStore = useCoursesStore();
        coursesStore.setSelectedCourses(this.selectedCourses);

        // Reset warning flag if no single session is still over the recommended load
        if (this.maxStandardCoursesPerSession() <= 2) {
          this.hasShownOverloadWarning = false;
        }
      }
    },

    /**
     * Handle offering selection (when course has multiple offerings)
     */
    handleOfferingSelection({ course, offering }) {
      // Add the course with the selected offering
      this.handleAddCourse({
        courseId: course.courseId,
        courseOfferingId: offering.courseOfferingId
      });
    },

    /**
     * Handle dropdown toggle - ensure only one dropdown is open at a time
     */
    handleDropdownToggle(uniqueCourseId, isOpen) {
      if (isOpen) {
        // Open this dropdown and close all others
        this.openDropdownCourseId = uniqueCourseId;
      } else {
        // Close this dropdown
        if (this.openDropdownCourseId === uniqueCourseId) {
          this.openDropdownCourseId = null;
        }
      }
    },

    /**
     * Handle review selection - check if more than 2 session-based courses (Standard type)
     * Uses V2 API structure with courseType field
     */
    handleReviewSelection() {
      this.logInfo('Checking course load before proceeding');

      this.dialog = false;
      this.proceedAfterOverload = true; // Mark that we should proceed after confirmation

      const maxPerSession = this.maxStandardCoursesPerSession();
      this.logInfo('Max Standard courses in any single session', { maxPerSession });

      const hasOverload = maxPerSession > 2;

      // If overload and we haven't already warned the user, show dialog
      if (hasOverload && !this.hasShownOverloadWarning) {
        this.dialog = true;
      } else {
        this.confirmEnrollment();
      }
    },

    /**
     * Confirm enrollment and navigate to dashboard
     */
    async confirmEnrollment() {
      let query = {};

      try {
        // Enroll only selectable sessions — checked per selected offering so a past
        // enrollment on another session of the same course cannot drop the new one
        const modifiedEnrollmentsList = this.selectedCourses
          .filter((course) => {
            const selectedId = course.offering?.courseOfferingId;
            const selectedOffering =
              selectedId != null ? course.offerings?.find((o) => String(o.offeringId) === String(selectedId)) : null;

            return !!selectedOffering && selectedOffering.disabled !== true;
          })
          .map((course) => {
            // Transform V2 API format to legacy format expected by connector
            const enrollmentPayload = {
              _courseOfferingId: course.offering?.courseOfferingId,
              _courseType: course.courseType,
              // New enrollments start unpaid; the backend upgrades covered learners to verified
              _enrollmentMode: 'honor',
              _isSelected: true,
              _coursePrefix: course.coursePrefix,
              _courseName: course.courseName,
              _courseStartDate: course.offering?.startDate,
              _asuCredits: course.credits,
              _enrollmentType: course.payment?.enrollmentType
            };

            return enrollmentPayload;
          });

        this.logInfo('Enrolling selected courses', {
          total: this.selectedCourses.length,
          selected: this.selectedCourses,
          newEnrollments: modifiedEnrollmentsList.length,
          courses: modifiedEnrollmentsList
        });

        if (modifiedEnrollmentsList.length === 0) {
          this.logInfo('No enrollable selections, skipping enrollment call');
          this.$router.push({ path: '/dashboard', query: {} });
          return;
        }

        // Call enrollment API (from old AddCourses.vue)
        const enrollmentData = await this.$conn.refreshAndAddEnrollmentToSFForSelectedCourses(modifiedEnrollmentsList);

        this.logInfo('Enrollment successful', enrollmentData);

        // Reset enrollment cache
        this.$conn.enrollmentList.resetActiveInactiveEnrollments();

        // Set query param for accelerate partners on successful enrollment
        const userStore = useUserStore();
        query = Helper.methods.isAcceleratePartner(userStore.apis.learnerData?._partnerURLSubDomain)
          ? { newCourseAdded: true }
          : {};

        this.$router.push({
          path: '/dashboard',
          query
        });
      } catch (error) {
        this.logError('Failed to enroll courses', error);

        if (useViewAsStore().isActive) {
          // Stay on this page instead of silently landing on the dashboard as if enrollment succeeded.
          useUiStore().showErrorNotification('Enrollment failed', error?.response?.data?.message);
          return;
        }

        this.$router.push({ path: '/dashboard', query: {} });
      }
    },

    onScroll() {
      // Handle scroll event
    },

    addTop() {
      const elem = document.getElementsByClassName('enrollment-section');
      if (elem.length) {
        this.checkEnrollmentSectionHeight = parseInt(window.getComputedStyle(elem[0], null).height) + 250;
      }
    },

    removeTop() {
      const elem = document.getElementsByClassName('enrollment-section');
      if (elem.length) {
        this.checkEnrollmentSectionHeight = parseInt(window.getComputedStyle(elem[0], null).height) - 250;
      }
    },

    handleReturnToDashboard() {
      this.$router.push('/dashboard');
    },

    redirectToCatalogPage() {
      window.open('https://catalog.apps.asu.edu/catalog/classes', '_blank');
    }
  },

  created() {
    this.init('addCourses', async () => {
      try {
        await this.initializeCoursesData();
      } catch (error) {
        const uiStore = useUiStore();
        uiStore.showErrorNotification('Error', 'Failed to load courses. Please check your connection and try again.');
        this.logError('Failed to initialize courses data', error);
      }
    });
  }
});
</script>
